import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import UIkit from "uikit";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";

import OpsSubscriptionRequired, {
  hasOpsSubscription,
} from "../../../common/OpsSubscriptionRequired";
import RecordsPerPage from "../../../common/RecordsPerPage";
import Pagination from "../../../common/Pagination";
import { AppContexts } from "../../../../providers";
import StorageService from "../../../../services/storage.service";
import {
  DEFAULT_RPP,
  MOVEMENT_TYPE_OPTIONS,
  PROPERTY_OPTIONS,
  formatDateTime,
  labelFor,
  optionName,
  propertyName,
} from "../data";

const DATE_RANGE_OPTIONS = [
  { label: "All Time", value: "all" },
  { label: "Last 7 Days", value: "7" },
  { label: "Last 30 Days", value: "30" },
  { label: "Last 90 Days", value: "90" },
];

const movementBadgeStyle = {
  lineHeight: "1.2",
  minHeight: "auto",
  paddingBottom: "4px",
  paddingTop: "4px",
  textAlign: "center",
  whiteSpace: "normal",
};

const movementBadge = (movementType) => {
  if (["received", "returned"].includes(movementType)) {
    return <span className="uk-badge uk-background-success" style={movementBadgeStyle}>{labelFor(MOVEMENT_TYPE_OPTIONS, movementType)}</span>;
  }
  if (["damaged", "lost", "disposed", "discarded"].includes(movementType)) {
    return <span className="uk-badge uk-background-danger" style={movementBadgeStyle}>{labelFor(MOVEMENT_TYPE_OPTIONS, movementType)}</span>;
  }
  if (["consumed"].includes(movementType)) {
    return <span className="uk-badge uk-background-warning" style={movementBadgeStyle}>{labelFor(MOVEMENT_TYPE_OPTIONS, movementType)}</span>;
  }
  if (["transfer", "restock_property"].includes(movementType)) {
    return <span className="uk-badge uk-background-primary" style={movementBadgeStyle}>{labelFor(MOVEMENT_TYPE_OPTIONS, movementType)}</span>;
  }
  return <span className="uk-badge" style={movementBadgeStyle}>{labelFor(MOVEMENT_TYPE_OPTIONS, movementType)}</span>;
};

export default function StorageMovementsIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const { user: account } = useContext(AppContexts.AuthContext);
  const opsSubscriptionLocked = !hasOpsSubscription(account);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [movementsLoadState, setMovementsLoadState] = useState("idle");
  const [criteria, setCriteria] = useState({
    search: "",
    location_id: "",
    item_id: "",
    movement_type: "",
    rental_id: "",
    date_range: "30",
    page: 0,
  });

  const loadMovements = useCallback(async () => {
    try {
      setMovementsLoadState("loading");
      const resp = await StorageService.listMovements({ page: 0, per: 1000 });
      const rows = resp.data?.paginated || [];
      setMovements(rows);
      setMovementsLoadState(rows.length ? "loaded" : "empty");
    } catch (err) {
      setMovementsLoadState("error");
      UIkit.notification("Failed to load stock movements.", "error");
    }
  }, []);

  const loadChoices = useCallback(async () => {
    try {
      const [locationsResp, itemsResp] = await Promise.all([
        StorageService.listLocations({ per: 1000 }),
        StorageService.listItems({ per: 1000 }),
      ]);
      setLocations(locationsResp.data?.paginated || []);
      setItems(itemsResp.data?.paginated || []);
    } catch (err) {
      UIkit.notification("Failed to load movement filters.", "error");
    }
  }, []);

  useEffect(() => {
    document.title = "Stock Movements - RueBaRue";
    loadChoices();
    loadMovements();
  }, [loadChoices, loadMovements]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page: [
        "search",
        "location_id",
        "item_id",
        "movement_type",
        "rental_id",
        "date_range",
      ].includes(key)
        ? 0
        : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const enrichedMovements = useMemo(() => {
    return movements.map((movement) => {
      const item = items.find((candidate) => candidate.id === movement.item_id) || {};
      const userName = [movement.user_first_name, movement.user_last_name]
        .filter(Boolean)
        .join(" ");
      return {
        ...movement,
        item_name: movement.item_name || item.name,
        sku: movement.sku || item.sku,
        user_name: movement.user_name || userName,
        from_location_name: movement.from_location_name || optionName(locations, movement.from_location_id),
        to_location_name: movement.to_location_name || optionName(locations, movement.to_location_id),
      };
    });
  }, [movements, items, locations]);

  const filtered = useMemo(() => {
    const now = new Date();

    return enrichedMovements.filter((movement) => {
      if (criteria.date_range !== "all") {
        const days = Number(criteria.date_range) || 0;
        if (days > 0 && movement.occurred_at) {
          const cutoff = new Date(now);
          cutoff.setDate(cutoff.getDate() - days);
          if (new Date(movement.occurred_at) < cutoff) return false;
        }
      }

      if (
        criteria.location_id &&
        String(movement.from_location_id || "") !== criteria.location_id &&
        String(movement.to_location_id || "") !== criteria.location_id
      ) {
        return false;
      }
      if (criteria.item_id && String(movement.item_id || "") !== criteria.item_id) return false;
      if (criteria.movement_type && movement.movement_type !== criteria.movement_type) return false;
      if (criteria.rental_id && String(movement.rental_id || "") !== criteria.rental_id) return false;

      if (criteria.search) {
        const query = criteria.search.toLowerCase();
        const haystack = `${movement.id || ""} ${movement.item_name || ""} ${movement.sku || ""} ${
          movement.from_location_name || ""
        } ${movement.to_location_name || ""} ${movement.user_name || ""} ${
          movement.reason || ""
        } ${movement.note || ""} ${movement.guest_reservation_id || ""} ${
          movement.work_order_id || ""
        }`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });
  }, [enrichedMovements, criteria]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / recordsPerPage));
  const pageRows = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage,
  );

  const filterConfigs = [
    [
      "location_id",
      [{ label: "All Locations", value: "" }, ...locations.map((loc) => ({ label: loc.name, value: String(loc.id) }))],
      "All Locations",
    ],
    [
      "item_id",
      [{ label: "All Items", value: "" }, ...items.map((item) => ({ label: item.name, value: String(item.id) }))],
      "All Items",
    ],
    ["movement_type", MOVEMENT_TYPE_OPTIONS, "All Types"],
    ["rental_id", [{ label: "All Properties", value: "" }, ...PROPERTY_OPTIONS], "All Properties"],
    ["date_range", DATE_RANGE_OPTIONS, "Last 30 Days"],
  ];

  return (
    <section>
      <div id="hms-page-title">
        <h1 className="uk-heading-small">Stock Movements</h1>
      </div>

      {opsSubscriptionLocked && <OpsSubscriptionRequired />}

      <div id="hms-main-body">
        <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
          <div className="uk-overflow-auto">
            <Flex alignment="between top" className="guest-form-top-filters">
              <form
                className="uk-form uk-flex uk-flex-middle hms-form uk-flex-wrap"
                onSubmit={(event) => event.preventDefault()}
              >
                <div className="uk-inline uk-margin-small-right uk-margin-small-bottom">
                  <div className="uk-search uk-search-default uk-form-custom">
                    {criteria.search ? (
                      <button
                        type="button"
                        className="uk-form-icon-flip uk-form-icon"
                        uk-icon="icon: close"
                        onClick={() => handleCriteriaChange("search", "")}
                        aria-label="Clear search"
                      ></button>
                    ) : (
                      <span className="uk-search-icon-flip" uk-search-icon="true" />
                    )}
                    <input
                      className="uk-search-input"
                      type="search"
                      placeholder="Search audit trail"
                      value={criteria.search}
                      onChange={(event) => handleCriteriaChange("search", event.target.value)}
                    />
                  </div>
                </div>

                {filterConfigs.map(([key, options, fallback]) => (
                  <div
                    key={key}
                    className="uk-inline uk-margin-small-right uk-margin-small-bottom"
                    uk-form-custom="target: > * > span:first-child"
                  >
                    <select
                      value={criteria[key]}
                      onChange={(event) => handleCriteriaChange(key, event.target.value)}
                    >
                      {options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                      <span className="select-value">
                        {criteria[key] ? labelFor(options, criteria[key]) : fallback}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                ))}
              </form>
            </Flex>

            <table className="uk-table uk-table-hover uk-table-divider hms-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th className="uk-table-expand">Item</th>
                  <th>Qty</th>
                  <th>From</th>
                  <th>To</th>
                  <th>User</th>
                  <th>Property</th>
                  <th>Reservation</th>
                  <th>Task</th>
                  <th>Reason</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={11}>
                      <div className="uk-text-center uk-padding-small uk-text-meta">
                        {movements.length > 0
                          ? "No movements match the current filters."
                          : movementsLoadState === "error"
                            ? "Stock movements could not be loaded from the API."
                            : "No stock movements returned from the API."}
                      </div>
                    </td>
                  </tr>
                )}

                {pageRows.map((movement) => (
                  <tr key={movement.id}>
                    <td>
                      {formatDateTime(movement.occurred_at)}
                      <div className="uk-margin-small-top">
                        {movementBadge(movement.movement_type)}
                      </div>
                    </td>
                    <td>
                      {movement.item_name || "-"}
                      <div className="uk-text-meta">{movement.sku || "No SKU"}</div>
                    </td>
                    <td>{movement.quantity ?? "-"}</td>
                    <td>
                      {movement.from_location_name || "-"}
                      {movement.from_after != null && (
                        <div className="uk-text-meta">
                          {movement.from_before} to {movement.from_after}
                        </div>
                      )}
                    </td>
                    <td>
                      {movement.to_location_name || "-"}
                      {movement.to_after != null && (
                        <div className="uk-text-meta">
                          {movement.to_before} to {movement.to_after}
                        </div>
                      )}
                    </td>
                    <td>{movement.user_name || "-"}</td>
                    <td>{propertyName(movement.rental_id)}</td>
                    <td>{movement.guest_reservation_id || "-"}</td>
                    <td>{movement.work_order_id || "-"}</td>
                    <td>
                      {movement.reason || "-"}
                      {movement.note && <div className="uk-text-meta">{movement.note}</div>}
                    </td>
                    <td>{movement.source || "Manual"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Flex alignment="between top" className="guest-form-top-filters">
              <Pagination
                count={totalPages}
                index={Math.min(criteria.page, totalPages - 1)}
                onIndexChanged={handleIndexChanged}
              />
              <RecordsPerPage />
            </Flex>
          </div>
        </div>
      </div>
    </section>
  );
}
